import { WorkspaceRateLimit, useWorkspaceRateLimitsQuery } from '@/types/generated/graphql';
import { AppProgressBarCircle } from '@components/app-progressbar-circle';
import { getStrokeColor, progressColor } from '@components/app-progressbar-circle.utils';
import { DotSeperator } from '@components/dot-seperator';
import { AppModal } from '@components/form/Modal';
import { cacheOnly } from '@shared/constants';
import { useSupportChat } from '@shared/hooks/use-support-chat';
import { List, Progress } from 'antd';
import clsx from 'clsx';
import { RiInformationFill } from 'react-icons/ri';
import { getMaxUsedPercent, getParsedLimits } from './helper';
import { useTranslation } from 'react-i18next';
import { capitalize } from '@shared/utils/capitalize';

type Props = {
  open: boolean;
  closeModal: VoidFunction;
};

function UsageLimitModal({ open, closeModal }: Props) {
  const { openSupportChat } = useSupportChat();
  const { data } = useWorkspaceRateLimitsQuery(cacheOnly);
  const parsedLimits = getParsedLimits(data?.workspaceRateLimits?.data as WorkspaceRateLimit[]);
  const maxUsedPercent = getMaxUsedPercent(parsedLimits);
  const { t } = useTranslation();

  return (
    <AppModal
      title={
        <>
          <p>{capitalize(t('usage_limit'))}</p>
          <p className="text-sm">{t('your_usage_across_calls_and_messages')}</p>
        </>
      }
      open={open}
      onCancel={closeModal}
      footer={
        <AppModal.Footer>
          <AppModal.Button className="w-full capitalize!" onClick={closeModal}>
            {t('cancel')}
          </AppModal.Button>
          <AppModal.Button
            type="primary"
            className="w-full bg-red-600! text-white!"
            htmlType="submit"
            onClick={() => {
              closeModal();
              openSupportChat();
            }}
          >
            {t('verify_kyc')}
          </AppModal.Button>
        </AppModal.Footer>
      }
    >
      <div className="space-y-4">
        <div className="bg-red-50 p-5">
          <div className="flex gap-4 items-center">
            <AppProgressBarCircle percent={maxUsedPercent} size={40} iconProps={{ size: 24 }} />
            <div>
              <p className="text-2xl">{maxUsedPercent}%</p>
              <p className="capitalize!">{t('usage_limit_consumed')}</p>
            </div>
          </div>
        </div>
        <div>
          <List bordered>
            {parsedLimits.map((rateLimit) => {
              const strokeColor = getStrokeColor(rateLimit.percent);
              return (
                <List.Item key={`${rateLimit.direction}-${rateLimit.category}`}>
                  <div>
                    <div>
                      <p className="capitalize">{clsx(rateLimit.direction, rateLimit.category)}</p>
                    </div>
                    <div className="flex gap-2">
                      <p>
                        <Progress
                          type="circle"
                          percent={rateLimit.percent}
                          strokeWidth={20}
                          size={20}
                          strokeColor={
                            strokeColor == 'green'
                              ? progressColor.green
                              : strokeColor === 'yellow'
                                ? progressColor.yellow
                                : progressColor.red
                          }
                          className={clsx('app-progress', strokeColor)}
                        />{' '}
                        {rateLimit.percent}%
                      </p>
                      <DotSeperator />
                      <p>
                        {rateLimit.exhaust?.value}/{rateLimit.total} {rateLimit.category}s
                      </p>
                      <DotSeperator />
                      {rateLimit?.category === 'call' ? (
                        <p>{t('limit_seconds_max_duration_per_call', { limit: rateLimit.timeLimit })}</p>
                      ) : (
                        <p>{t('limit_characters_per_sms', { limit: 160 })}</p>
                      )}
                    </div>
                  </div>
                </List.Item>
              );
            })}
          </List>
        </div>
        <span className="flex gap-2 items-center">
          <RiInformationFill color="var(--color-red-500)" />
          <p className="text-sm">{t('verify_kyc_to_remove_call_and_messaging_usage_limits')}</p>
        </span>
      </div>
    </AppModal>
  );
}

export default UsageLimitModal;
